import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, IconButton, Slide, Stack } from '@mui/material';
import { Close, PlayCircleFilled } from '@mui/icons-material';

// How long after landing on a page before the nudge first appears.
const FIRST_SHOW_MS = 10000;

// How long it stays away after the customer dismisses it with the ×.
const REAPPEAR_AFTER_DISMISS_MS = 10000;

// How long after the customer returns to the browser tab before it reappears.
const REAPPEAR_AFTER_TAB_RETURN_MS = 10000;

const VideoReminderPopup = ({ active, remaining, total, onResume }) => {
  const [visible, setVisible] = useState(false);

  // Bumping this re-runs the scheduling effect even when `visible` did not
  // change. Without it, re-arming while already hidden is a no-op — setVisible
  // (false) on an already-false state does not re-render, so no new timer is
  // ever scheduled and the popup never comes back.
  const [scheduleTick, setScheduleTick] = useState(0);
  const delayRef = useRef(FIRST_SHOW_MS);

  const rearm = useCallback((delayMs) => {
    delayRef.current = delayMs;
    setVisible(false);
    setScheduleTick((n) => n + 1);
  }, []);

  // Whenever the popup is hidden, schedule the next appearance.
  useEffect(() => {
    if (!active) {
      setVisible(false);
      return;
    }
    if (visible) return;

    const t = setTimeout(() => setVisible(true), delayRef.current);
    return () => clearTimeout(t);
  }, [active, visible, scheduleTick]);

  // Start from the long delay each time the series becomes pending again.
  useEffect(() => {
    if (active) delayRef.current = FIRST_SHOW_MS;
  }, [active]);

  // Coming back to the tab re-arms it, whether it was showing or not.
  useEffect(() => {
    if (!active) return;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        rearm(REAPPEAR_AFTER_TAB_RETURN_MS);
      }
    };
    // focus covers returning from another window, which does not always fire
    // visibilitychange.
    window.addEventListener('focus', onVisibilityChange);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', onVisibilityChange);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [active, rearm]);

  if (!active) return null;

  const watched = Math.max(0, (total || 0) - (remaining || 0));

  return (
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 12, sm: 24 },
          bottom: { xs: 12, sm: 24 },
          zIndex: 1400,
          width: { xs: 'calc(100% - 24px)', sm: 340 },
          maxWidth: 360,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: 6,
          bgcolor: 'background.paper',
          borderLeft: '4px solid #e67e22',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <PlayCircleFilled sx={{ color: '#e67e22', fontSize: 28, mt: '2px' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#222', pr: 3 }}>
                You haven&apos;t watched these videos yet
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: '#666', mt: 0.5 }}>
                {watched > 0
                  ? `${remaining} of ${total} videos still to watch. Watch them now.`
                  : `A short ${total}-video series is waiting for you. Watch them now.`}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label="Dismiss reminder"
              onClick={() => rearm(REAPPEAR_AFTER_DISMISS_MS)}
              sx={{ color: '#999', mt: '-4px', mr: '-4px' }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Stack>

          <Button
            fullWidth
            onClick={onResume}
            sx={{
              mt: 1.5,
              backgroundColor: '#e67e22',
              color: '#fff',
              py: 1,
              fontWeight: 'bold',
              fontSize: 12.5,
              fontFamily: 'Inter-Medium',
              letterSpacing: '0.6px',
              borderRadius: 1.5,
              '&:hover': { backgroundColor: '#d35400' },
            }}
          >
            {watched > 0 ? 'Continue watching' : 'Watch now'}
          </Button>
        </Box>
      </Box>
    </Slide>
  );
};

export default VideoReminderPopup;
